import { ProjectListingTemplate as template } from './project-listing.template';
import { ProjectListingStyles as styles } from './project-listing.styles';
import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { Project } from '../../../../types';
import { updateArray } from '../../../../utils/dataserver';
import { IProjectService } from '../../../../services';
import { IEnumService } from '../../../../services/enum.service';

const name = 'project-listing';

interface IProjectGrid extends Project {
  DETAILS: {
    ROW_REF: string;
    OPERATION: string;
  };
}

@customElement({
  name,
  template,
  styles,
})
export class ProjectListing extends FASTElement {
  @IEnumService enumService: IEnumService;
  @IProjectService projectService: IProjectService;
  @attr @observable activeTabId: string;

  @observable projectListingGrid: zeroAgGrid;
  @observable selectedProjectId: string = 'DEFAULT';

  private projectList: Array<IProjectGrid> = [];
  private projectSubscription: SocketSubscription;

  connectedCallback(): void {
    super.connectedCallback();
    const gridOptions = {
      animateRows: true,
      suppressRowClickSelection: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      tooltipShowDelay: 300,
    };

    this.projectListingGrid.gridOptions = gridOptions;
  }

  public onGridReady() {
    this.streamProjects();
  }

  disconnectedCallback(): void {
    this.projectSubscription?.unsubscribe();
    super.disconnectedCallback();
  }

  private streamProjects() {
    this.projectSubscription = this.projectService.getAllProjects('').subscribe({
      next: (streamMessage: Message) => {
        const resultList = streamMessage.ROW ?? null;
        if (resultList) {
          this.projectList = updateArray(resultList, this.projectList);
        } else {
          const resultERROR = streamMessage.DETAILS?.ERROR;
          logger.error(resultERROR);
        }
        this.projectListingGrid.gridApi.setRowData(this.projectList);
        this.projectListingGrid.gridApi.setPinnedTopRowData([
          {
            NAME: 'Any',
            AVAILABLE_CAPACITY_AC: null,
            CITY_NAME: '',
            COUNTRY_NAME: '',
            COUNTY_NAME: '',
            CREATED_BY: '',
            GUARANTEED_COD: null,
            HUB_HEIGHT: null,
            INTERCONNECTION_POINT: '',
            INTERCONNECTION_QUEUE_ID: '',
            INTERCONNECTION_VOLTAGE: '',
            ISO: '',
            LATITUDE: null,
            LEGAL_NAME: '',
            LONGITUDE: null,
            MEGAWATT_AC: null,
            MEGAWATT_DC: null,
            OWNER: '',
            P50_CAPACITY_FACTOR: null,
            PANEL_TECHNOLOGY: '',
            PANEL_TYPE: '',
            PANEL_MAKE_AND_MODEL: '',
            PROJECT_ID: 'DEFAULT',
            PROJECT_OPERATIONAL_STATUS: null,
            PROJECT_OPERATOR: '',
            HUB_ZONE_MATRIX_ID: '',
            SETTLEMENT_INDEX: '',
            SETTLEMENT_POINT: '',
            STATE_NAME: '',
            STATE_ABBREVIATION: '',
            TARGET_COD: null,
            TECHNOLOGY: null,
            TRACKING_TYPE: '',
            TURBINE_COUNT: null,
            TURBINE_DIAMETER: null,
            TURBINE_MAKE_AND_MODEL: '',
            TURBINE_MEGAWATT: null,
            CREATED_AT: null,
            MODIFIED_BY: '',
            MODIFIED_AT: null,
            APPLICABLE_REC_PROGRAM: '',
            DRAFT_PROJECT_ID: '',
            TRACKING_TYPE_STRING: '',
            PANEL_TYPE_STRING: '',
            PANEL_TECHNOLOGY_STRING: '',
            ISO_STRING: '',
            SETTLEMENT_INDEX_STRING: '',
            INTERCONNECTION_VOLTAGE_STRING: '',
            DETAILS: {
              ROW_REF: '0',
              OPERATION: 'INSERT',
            },
          },
        ]);
      },

      error: (error: any) => {
        logger.error(error);
      },
    });
  }

  public selectProject(project: Project) {
    this.$emit('projectSelected', project.PROJECT_ID !== 'DEFAULT' ? project : null);
    this.selectedProjectId = project.PROJECT_ID;
    this.projectListingGrid.gridApi.redrawRows();
  }
}
